import {useSortable} from '@dnd-kit/sortable';
import {openDropdown, Tooltip, useTranslationContext} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {use, useRef, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {HEADER_CELL_BORDER_BOTTOM} from '../../../../constants/borderStyles';
import {DEFAULT_COL_WIDTH} from '../../../../constants/defaultColWidth';
import {ColumnMenuContext, useColumnMenuContext} from '../../../../context/ColumnMenuContext';
import {DataGridReactContext, useDataGridContext} from '../../../../context/useDataGridContext';
import {EagGridConfigReactContext} from '../../../../context/useEagGridConfig';
import {useIsMobileView} from '../../../../hooks/useIsMobileView';
import {EagGridStoreContext} from '../../../../store/EagGridStoreContext';
import {
  useColumnMovingAllowed,
  useColumnResizingAllowed,
  useColumnSortMode,
  useClipboardExportAllowed,
  useGridSettingsSafe,
  useSortedColumnCount,
} from '../../../../store/selectors';
import {selectCellResizeState} from '../../../../store/selectors/selectCellResizeState';
import {useEagGridSelector} from '../../../../store/useEagGridSelector';
import {useEagGridStore} from '../../../../store/useEagGridStore';
import type {EagColumn} from '../../../../types/EagColumn';
import type {EagColumnState} from '../../../../types/EagColumnState';
import {getPinnedEdgeShadowClass} from '../../../../utils/getPinnedEdgeShadowClass';
import {getSortModel} from '../../../../utils/getSortModel';
import {getRowPaddingClass, HeaderHeightToPx} from '../../../../utils/lineHeightToValue';
import {ColumnHeaderMenu} from './components/ColumnHeaderMenu/ColumnHeaderMenu';
import {HeaderCellActions} from './components/HeaderCellActions/HeaderCellActions';
import {HeaderCellDecorators} from './components/HeaderCellDecorators/HeaderCellDecorators';
import {ResizeHandle} from './components/ResizeHandle/ResizeHandle';
import {SortReplacementPopover} from './components/SortReplacementPopover/SortReplacementPopover';
import {computeHeaderCellLayout} from './utils/computeHeaderCellLayout';
import {computeHeaderCellPinnedStyle} from './utils/computeHeaderCellPinnedStyle';
import {getColumnMenuMaxHeight} from './utils/getColumnMenuMaxHeight';
import {isEventFromResizeHandle} from './utils/isEventFromResizeHandle';

function getAriaSort(
  sort: EagColumnState['sort'],
  columnSortable: boolean | undefined,
  columnSortMode: string
): 'ascending' | 'descending' | 'none' | undefined {
  if (columnSortMode === 'NONE') {
    return undefined;
  }
  return match(sort)
    .with('ASC', () => 'ascending' as const)
    .with('DESC', () => 'descending' as const)
    .otherwise(() => (columnSortable ? ('none' as const) : undefined));
}

type HeaderCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  colIndex: number;
  onSort: (key: string, additive: boolean) => void;
  onResizeStart: (key: string, x: number, colIndex: number, resizeFromLeft?: boolean) => void;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  // Pinning edge shadow — true on the boundary column of each pinned group
  isLastPinnedLeft?: boolean;
  isFirstPinnedRight?: boolean;
  /** Sticky top offset in px. 0 when no group header row is shown; GROUP_HEADER_ROW_HEIGHT when visible. */
  stickyTop: number;
  /**
   * Previous visible column in the non-right-pinned sequence. Enables the companion
   * resize handle on this cell's LEFT edge that resizes the PREVIOUS column — together
   * with the previous cell's own right-edge handle the grab target straddles the
   * column border, which is where users actually aim.
   */
  previousColumn?: EagColumn;
  /** `<col>` DOM index of {@link previousColumn} in the colgroup. */
  previousColIndex?: number;
};

// eslint-disable-next-line max-statements -- Pre-existing violation
export function HeaderCell(props: HeaderCellProps) {
  const t = useTranslationContext();
  const gridStore = useEagGridStore();
  // Drag state is no longer subscribed here — drag visuals are applied via a
  // <style> element injected by useGridDragAndDrop, scoped to both
  // td[data-column="..."] and th[data-column="..."]. Local `isDragging` from
  // useSortable still drives the source-column's cursor change.
  const {isResizing, resizingColumn} = useEagGridSelector(selectCellResizeState);
  const gridSettings = useGridSettingsSafe();
  const heightPx = HeaderHeightToPx[gridSettings.headerHeight];
  const paddingClass = getRowPaddingClass[gridSettings.headerHeight];

  // Behavior flags
  const columnResizingAllowed = useColumnResizingAllowed();
  const columnMovingAllowed = useColumnMovingAllowed();
  const columnSortMode = useColumnSortMode();
  const sortedColumnCount = useSortedColumnCount();

  // Column menu state and callbacks — provided via ColumnMenuContext by EagGridTable.
  // No prop drilling through HeaderRow.
  const columnMenu = useColumnMenuContext();
  const clipboardExportAllowed = useClipboardExportAllowed();
  const isMobile = useIsMobileView();

  // Captured for re-provision inside the right-click dropdown: openDropdown renders
  // its content under the app-level DropdownProvider, which sits ABOVE
  // EagGridConfigProvider / the grid's DataGridContext — without re-providing them,
  // any filter widget that fetches options (useHttpCalls → strict useEagGridConfig)
  // throws and blanks the page. Read via the raw context (non-strict): HeaderCell
  // itself must render fine without the provider (unit tests); the strict check
  // stays where the value is actually consumed.
  const gridConfig = use(EagGridConfigReactContext);
  const dataGridContext = useDataGridContext();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pendingSort, setPendingSort] = useState<{
    sortKey: string;
    returnFocusTo: HTMLElement;
  } | null>(null);
  const thRef = useRef<HTMLTableCellElement | null>(null);

  const {attributes, listeners, setNodeRef, isDragging} = useSortable({
    id: props.column.key,
    // Disable animated transforms — body cells cannot follow the header transform,
    // so we use DragOverlay for the full-column visual instead.
    animateLayoutChanges: () => false,
  });

  const width = props.columnState.width ?? DEFAULT_COL_WIDTH;

  const {
    useSmallButtons,
    showMenuButton,
    showSortButton,
    textPaddingRight,
    isColumnFilterActive: _isColumnFilterActive,
    menuPriority,
    columnHasMenuContent,
  } = computeHeaderCellLayout({
    column: props.column,
    columnState: props.columnState,
    width,
    headerHeight: gridSettings.headerHeight,
    showColumnMenu: columnMenu.showColumnMenu,
    showColumnCopy: clipboardExportAllowed,
    columnSortMode,
    isMobile,
    filterState: columnMenu.filterState,
  });

  const isPinned = props.columnState.pinned === 'LEFT' || props.columnState.pinned === 'RIGHT';
  const isEdge = (props.isLastPinnedLeft ?? false) || (props.isFirstPinnedRight ?? false);
  const shadowClass = getPinnedEdgeShadowClass(props.columnState.pinned, isEdge);

  // stickyTop is 0 normally, GROUP_HEADER_ROW_HEIGHT when the group header row is visible —
  // so the main header row sticks below the group row instead of overlapping it.
  const pinnedStyle = computeHeaderCellPinnedStyle({
    pinned: props.columnState.pinned,
    stickyTop: props.stickyTop,
    pinnedLeftOffset: props.pinnedLeftOffset,
    pinnedRightOffset: props.pinnedRightOffset,
  });

  const ariaSortValue = getAriaSort(props.columnState.sort, props.column.sortable, columnSortMode);

  const showSortNumber = columnSortMode === 'MULTI_SORT' && sortedColumnCount >= 2;
  const sortNumber =
    showSortNumber && props.columnState.sortIndex != null
      ? props.columnState.sortIndex + 1
      : undefined;

  const isSortable = props.column.sortable === true && columnSortMode !== 'NONE';

  const requestSort = (event: React.MouseEvent<HTMLElement>) => {
    if (
      columnSortMode === 'MULTI_SORT' &&
      !event.shiftKey &&
      sortedColumnCount > 1 &&
      gridSettings.confirmSortReplacement !== false
    ) {
      setPendingSort({
        sortKey: JSON.stringify(getSortModel(gridStore.getState().columnStates)),
        returnFocusTo: event.currentTarget,
      });
      return;
    }
    setPendingSort(null);
    props.onSort(props.column.key, event.shiftKey);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    requestSort(event);
  };

  const confirmSortReplacement = () => {
    const expectedSortKey = pendingSort?.sortKey;
    setPendingSort(null);
    if (expectedSortKey === JSON.stringify(getSortModel(gridStore.getState().columnStates))) {
      props.onSort(props.column.key, false);
    }
  };

  const handleHeaderClick = (event: React.MouseEvent<HTMLTableCellElement>) => {
    // A click landing on a resize handle (cursor shows col-resize there) must never sort.
    if (isSortable && !isEventFromResizeHandle(event)) {
      requestSort(event);
    }
  };

  const handleHeaderContextMenu = (event: React.MouseEvent) => {
    if (!columnMenu.showColumnMenu) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    openDropdown(
      event,
      <EagGridStoreContext.Provider value={gridStore}>
        <EagGridConfigReactContext.Provider value={gridConfig}>
          <DataGridReactContext.Provider value={dataGridContext}>
            <ColumnMenuContext.Provider value={columnMenu}>
              <ColumnHeaderMenu
                column={props.column}
                columnPinned={props.columnState.pinned}
                isOpen
                onClose={() => {
                  // openDropdown manages its own close lifecycle
                }}
                // openDropdown anchors the menu at the cursor point, so the
                // cursor Y is the anchor bottom (T20-136597).
                maxHeight={getColumnMenuMaxHeight(event.clientY)}
              />
            </ColumnMenuContext.Provider>
          </DataGridReactContext.Provider>
        </EagGridConfigReactContext.Provider>
      </EagGridStoreContext.Provider>,
      // Same flags as the button-based ColumnMenuDropdown (HeaderCellActions):
      // floating-ui's typeahead/list-navigation preventDefaults printable keys,
      // which blocks typing into the Filter tab's inputs. Same boundary too —
      // the menu must stay horizontally inside the grid.
      {
        isKeyboardNavigationDisabled: true,
        isFocusManagementDisabled: true,
        boundary: columnMenu.menuBoundary ?? undefined,
      }
    );
  };

  const isRightPinned = props.columnState.pinned === 'RIGHT';

  const handleResizePointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
    props.onResizeStart(props.column.key, event.clientX, props.colIndex, isRightPinned);
  };

  const handlePreviousColumnResizePointerDown = (event: React.PointerEvent) => {
    if (isNil(props.previousColumn) || isNil(props.previousColIndex)) {
      return;
    }
    event.stopPropagation();
    props.onResizeStart(props.previousColumn.key, event.clientX, props.previousColIndex, false);
  };

  const isBeingResized = isResizing && resizingColumn === props.column.key;

  const showResizeHandle =
    columnResizingAllowed && props.column.resizable !== false && !isBeingResized;

  // Companion handle on the LEFT edge resizing the PREVIOUS column — widens the grab
  // target across the column border. Right-pinned cells already use their left edge
  // for their own handle, so the companion applies to the non-right-pinned sequence only.
  const showPreviousColumnResizeHandle =
    columnResizingAllowed &&
    !isRightPinned &&
    isNotNil(props.previousColumn) &&
    props.previousColumn.resizable !== false &&
    isNotNil(props.previousColIndex) &&
    resizingColumn !== props.previousColumn.key;

  // Sort indicator — always visible when the column is sorted. Rendered by
  // HeaderCellActions in the absolute right-aligned cluster, AFTER the
  // hover-revealed menu button, so it sits at the far right of the cell.
  // onPointerDown stops propagation to prevent dnd-kit drag initiation.
  const sortIndicator = showSortButton ? (
    <button
      className="flex-shrink-0 flex items-center gap-0.5 text-[#0065FF] cursor-pointer border-none bg-transparent p-0"
      onClick={handleSortClick}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={t('page.dataGrid.column.sortAriaLabel', {
        columnName: props.column.headerName,
      })}
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="#0065FF" aria-hidden="true">
        {props.columnState.sort === 'ASC' ? <path d="M5 2L9 8H1Z" /> : <path d="M5 8L1 2H9Z" />}
      </svg>
      <Show when={sortNumber != null}>
        <span className="text-[10px] font-bold leading-none">{sortNumber}</span>
      </Show>
    </button>
  ) : undefined;

  // Drop-target background is applied via injected CSS (see useGridDragAndDrop).
  const bgClass = 'bg-[#FAFBFC]';
  const isHeaderWrap = gridSettings.headerWrapMode === 'WRAP';
  const headerTextClass = isMobile ? 'text-[11px]' : 'text-xs';

  const thStyle: React.CSSProperties = {
    width,
    maxWidth: width,
    height: heightPx,
    maxHeight: heightPx,
    overflow: 'hidden',
    ...pinnedStyle,
    ...HEADER_CELL_BORDER_BOTTOM,
  };

  return (
    <th
      ref={(node) => {
        setNodeRef(node);
        thRef.current = node;
      }}
      className={`group/header relative ${headerTextClass} font-semibold text-[#42526E] text-left p-0 select-none ${isHeaderWrap ? '' : 'text-ellipsis whitespace-nowrap'} ${isSortable ? 'cursor-pointer' : ''} ${bgClass} ${shadowClass}`}
      scope="col"
      style={thStyle}
      {...attributes}
      role="columnheader" // eslint-disable-line jsx-a11y/no-redundant-roles -- Muted pre-existing violation
      aria-sort={ariaSortValue}
      data-column={props.column.key}
      onClick={handleHeaderClick}
      onContextMenu={handleHeaderContextMenu}
    >
      {/* Text area — drag listeners for column reorder, NO sort-on-click.
          cursor-grab signals draggability on hover; cursor-grabbing while the
          column is being dragged.  Suppressed entirely when columnMovingAllowed
          is false, since dnd-kit's DndContext isn't mounted in that mode and
          the listeners are no-ops — showing a grab cursor would be misleading. */}
      <div
        className={`flex items-center h-full min-w-0 overflow-hidden ${paddingClass} ${
          columnMovingAllowed ? (isDragging ? 'cursor-grabbing' : 'cursor-grab') : '' // eslint-disable-line no-nested-ternary -- Pre-existing violation
        }`}
        style={{paddingRight: textPaddingRight}}
        {...listeners}
      >
        <span className="min-w-0 flex-1 overflow-hidden">
          <Tooltip label={props.column.headerName}>
            <span className={isHeaderWrap ? 'block break-words line-clamp-2' : 'truncate block'}>
              {props.column.headerName}
            </span>
          </Tooltip>
        </span>
      </div>

      <HeaderCellActions
        column={props.column}
        columnPinned={props.columnState.pinned}
        isMobile={isMobile}
        useSmallButtons={useSmallButtons}
        showMenuButton={showMenuButton}
        showColumnMenu={columnMenu.showColumnMenu}
        columnHasMenuContent={columnHasMenuContent}
        menuPriority={menuPriority}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        sortIndicator={sortIndicator}
        menuBoundary={columnMenu.menuBoundary}
      />

      <HeaderCellDecorators
        isPinned={isPinned}
        isPinnedLeft={props.columnState.pinned === 'LEFT'}
        isRightPinned={isRightPinned}
        isLastPinnedLeft={props.isLastPinnedLeft ?? false}
        isBeingResized={isBeingResized}
        verticalSeparatorsVisible={gridSettings.verticalSeparatorsVisible}
      />
      {/* Right-pinned columns use a left-side handle — more accessible when the column is at the far right */}
      <Show when={showResizeHandle}>
        <ResizeHandle
          edge={isRightPinned ? 'LEFT' : 'RIGHT'}
          onResizePointerDown={handleResizePointerDown}
        />
      </Show>
      <Show when={showPreviousColumnResizeHandle}>
        <ResizeHandle edge="LEFT" onResizePointerDown={handlePreviousColumnResizePointerDown} />
      </Show>
      <Show when={pendingSort}>
        {(pendingSort) => (
          <SortReplacementPopover
            anchor={thRef.current}
            returnFocusTo={pendingSort.returnFocusTo}
            message={t('page.datagrid.labels.confirmReplaceSorting')}
            confirmLabel={t('general.actions.confirm')}
            cancelLabel={t('general.actions.cancel')}
            onConfirm={confirmSortReplacement}
            onClose={() => setPendingSort(null)}
          />
        )}
      </Show>
    </th>
  );
}
